import React, {useMemo, useState} from "react";
import {Alert, Button, Card, Form, Input, message, Radio, Select} from "antd";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {upperFirst} from "lodash";
import {AxiosError} from "axios";

// ----- Local calls -----
import {UsersService} from "services";
import {RootState} from "redux/state";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import {CREATE_USER_ROLES, ROLE_PERMISSION_MAPPING, ROLES, ROLES_MAPPING} from "../../../constants/roles";

// ----- Global variables -----
const formLayout = {
    labelCol: {span: 4},
    wrapperCol: {span: 8},
};

// ----- Types -----
type PasswordMode = 'setup-email' | 'password'

interface CreateUserValues {
    email: string
    username?: string
    role: string
    passwordMode: PasswordMode
    password?: string
}

// ----- Components -----
const CreateUserPage: React.FC = () => {
    // ----- Navigation -----
    const navigate = useNavigate();

    // ----- Store -----
    const authenticatedUser = useSelector((state: RootState) => state?.auth?.authenticatedUser);

    // ----- Data -----
    const [loading, setLoading] = useState(false);

    // ----- Form -----
    const [form] = Form.useForm<CreateUserValues>();
    const passwordMode = Form.useWatch('passwordMode', form);

    const canCreateUser = CREATE_USER_ROLES.includes(authenticatedUser?.role || '')

    const roleOptions = useMemo(() => {
        const allowedRoles = ROLE_PERMISSION_MAPPING[authenticatedUser?.role || 'user'] || []
        return ROLES.map((role: string) => ({
            value: role,
            label: upperFirst(role),
            disabled: allowedRoles.indexOf(role) === -1,
        }))
    }, [authenticatedUser?.role])

    // ----- Handlers -----
    const handleFinish = (values: CreateUserValues) => {
        if (!ROLES_MAPPING[values.role]) return

        setLoading(true)
        UsersService.createUser({
            email: values.email.trim(),
            username: values.username?.trim() || undefined,
            roleId: ROLES_MAPPING[values.role],
            password: values.passwordMode === 'password' ? values.password : undefined,
        })
            .then(res => {
                const user = res?.data?.user
                message.success(values.passwordMode === 'setup-email' ?
                    'User created, a setup email was sent' :
                    'User created successfully')
                navigate(user?.id ? `/users/${user.id}` : '/users')
            })
            .catch((err: AxiosError<{ errors?: unknown[] }>) => {
                console.error('Error while creating user, message:', err)
                const detail = err?.response?.data?.errors?.[0]
                message.error(typeof detail === 'string' ? detail : 'Error while creating user')
            })
            .finally(() => {
                setLoading(false)
            })
    }

    return (
        <>
            <CustomBreadcrumb list={['users', 'create']}/>
            <Card title="Create user">
                {!canCreateUser ?
                    <Alert type="warning" showIcon message="Only admins can create users"/> :
                    <Form
                        {...formLayout}
                        form={form}
                        onFinish={handleFinish}
                        initialValues={{role: 'factory-worker', passwordMode: 'setup-email'}}
                    >
                        <Form.Item name="email"
                                   label="Email"
                                   rules={[
                                       {required: true, message: 'Please enter an email'},
                                       {type: 'email', message: 'Please enter a valid email'},
                                   ]}
                        >
                            <Input placeholder="worker@example.com"/>
                        </Form.Item>
                        <Form.Item name="username" label="Name">
                            <Input placeholder="Optional"/>
                        </Form.Item>
                        <Form.Item name="role"
                                   label="Role"
                                   rules={[{required: true, message: 'Please select a role'}]}
                        >
                            <Select placeholder="Please select a role" options={roleOptions}/>
                        </Form.Item>
                        <Form.Item name="passwordMode" label="Password">
                            <Radio.Group>
                                <Radio value="setup-email">Send setup email</Radio>
                                <Radio value="password">Set password</Radio>
                            </Radio.Group>
                        </Form.Item>
                        {passwordMode === 'password' &&
                            <Form.Item name="password"
                                       label=" "
                                       colon={false}
                                       rules={[{required: true, message: 'Please enter a password'}]}
                            >
                                <Input.Password placeholder="Password"/>
                            </Form.Item>
                        }
                        <Form.Item wrapperCol={{offset: 4, span: 8}}>
                            <Button htmlType="submit" type="primary" loading={loading}>
                                Create user
                            </Button>
                        </Form.Item>
                    </Form>
                }
            </Card>
        </>
    )
}

export default CreateUserPage
